import {
  clearValidationForAllParameters, clearValidationForKeys, clearValidationForSingleParameter
} from 'app/area/shared/report/form/validation';
import { Reducer, AnyAction } from 'redux';
import {
  ACTION, ReceiveLanguageOptions,
  RequestImportSet, ReceiveImportSet, FailImportSet,
  RequestDeleteImportSet, ReceiveDeleteImportSet, FailDeleteImportSet,
  RequestSaveImportSet, ReceiveSaveImportSet, FailSaveImportSet,
  UpdateItem, ChangeLanguage, CopyLanguageToAll,
  ClearForm, UpdateValidation, ResetValidation
} from './action-types';
import { languageValidationParameter } from './constants';
import { ImportSetAdminFormState, ImportSetItem } from './types-store';
import { withOptionsReducer } from 'app/components/options-handler';
import { STATE } from 'app/constants';

export const INITIAL_STATE: ImportSetAdminFormState = {
  actionType: undefined,
  importSetItem: undefined,
  originalImportSetItem: undefined,
  fetching: false,
  deleting: false,
  saving: false,
  languageOptions: [],
  validation: {}
};

const reducers: { [indexer: string]: Reducer<ImportSetAdminFormState> } = {

  [ACTION.RECEIVE_LANGUAGE_OPTIONS]: (state: ImportSetAdminFormState,
    action: ReceiveLanguageOptions): ImportSetAdminFormState => {

    return {
      ...state,
      languageOptions: action.options
    };
  },

  [ACTION.REQUEST_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: RequestImportSet): ImportSetAdminFormState => {

    return {
      ...state,
      importSetItem: undefined,
      originalImportSetItem: undefined,
      fetching: true,
      saving: false,
      validation: {}
    };
  },

  [ACTION.RECEIVE_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: ReceiveImportSet): ImportSetAdminFormState => {

    return {
        ...state,
        importSetItem: action.item,
        originalImportSetItem: action.item,
        fetching: false
      };
  },

  [ACTION.FAIL_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: FailImportSet): ImportSetAdminFormState => {

    return {
        ...state,
        fetching: false
      };
  },

  [ACTION.REQUEST_DELETE_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: RequestDeleteImportSet): ImportSetAdminFormState => {

    return {
        ...state,
        deleting: true
      };
  },

  [ACTION.RECEIVE_DELETE_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: ReceiveDeleteImportSet): ImportSetAdminFormState => {

    return {
        ...state,
        deleting: false
      };
  },

  [ACTION.FAIL_DELETE_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: FailDeleteImportSet): ImportSetAdminFormState => {

    return {
        ...state,
        deleting: false
      };
  },

  [ACTION.REQUEST_SAVE_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: RequestSaveImportSet): ImportSetAdminFormState => {

    return {
      ...state,
      saving: true
    };
  },

  [ACTION.RECEIVE_SAVE_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: ReceiveSaveImportSet): ImportSetAdminFormState => {

    return {
        ...state,
        saving: false,
        originalImportSetItem: state.importSetItem        
      };
  },

  [ACTION.FAIL_SAVE_IMPORT_SET]: (state: ImportSetAdminFormState,
    action: FailSaveImportSet): ImportSetAdminFormState => {

    return {
        ...state,
        saving: false
      };
  },

  [ACTION.CHANGE_LANGUAGE]: (state: ImportSetAdminFormState,
    action: ChangeLanguage): ImportSetAdminFormState => {

    const importSetItem = state.importSetItem || {} as ImportSetItem;
    const language = importSetItem.language || [];

    return {
      ...state,
      importSetItem: {
        ...importSetItem,
        language: language.some(languageItem => languageItem.value === action.languageId)
          ? language.map(
            languageItem => languageItem.value === action.languageId
              ? {
                ...languageItem,
                description: action.description
              }
              : languageItem
          )
          : [
            ...language,
            {
              value: action.languageId,
              description: action.description
            }
          ]
      },
      validation: clearValidationForSingleParameter(state.validation, 'language', languageValidationParameter, action.languageId)
    };
  },

  [ACTION.COPY_LANGUAGE_TO_ALL]: (state: ImportSetAdminFormState,
    action: CopyLanguageToAll): ImportSetAdminFormState => {

    const importSetItem = state.importSetItem || {} as ImportSetItem;
    const language = importSetItem.language || [];
    const targetLanguage = language.find(languageItem => languageItem.value === action.languageId);

    if (targetLanguage === undefined) {
      return state;
    }

    const updatedLanguage = language.map(
      languageItem => ({
        ...languageItem,
        description: targetLanguage.description
      })
    );
    const newLanguage = (state.languageOptions || [])
      .map(option => option.value)
      .filter(languageId => !language.some(languageItem => languageItem.value === languageId))
      .map(languageId => ({
        value: languageId,
        description: targetLanguage.description
      }));

    return {
      ...state,
      importSetItem: {
        ...importSetItem,
        language: [
          ...updatedLanguage,
          ...newLanguage
        ]
      },
      validation: clearValidationForAllParameters(state.validation, 'language', languageValidationParameter)
    };
  },

  [ACTION.UPDATE_ITEM]: (state: ImportSetAdminFormState,
    action: UpdateItem): ImportSetAdminFormState => {

    const importSetItem = {
      ...(state.importSetItem || {}),
      ...action.item
    } as ImportSetItem;
    
    const validation = clearValidationForKeys(state.validation, Object.keys(action.item) as any[]);

    return {
      ...state,
      importSetItem,
      validation
    };
  },

  [ACTION.CLEAR_FORM]: (state: ImportSetAdminFormState,
    action: ClearForm): ImportSetAdminFormState => {

    return {
      ...state,
      ...INITIAL_STATE,
      languageOptions: state.languageOptions
    };
  },

  [ACTION.UPDATE_VALIDATION]: (state: ImportSetAdminFormState,
    action: UpdateValidation): ImportSetAdminFormState => {
    return {
      ...state,
      validation: { ...state.validation, ...action.validation }
    };
  },

  [ACTION.RESET_VALIDATION]: (state: ImportSetAdminFormState, action: ResetValidation): ImportSetAdminFormState => {
    return {
      ...state,
      validation: { ...state.validation, [action.key]: { messages: [] } }
    };
  }
};

const stateReducer: Reducer<ImportSetAdminFormState> = (
  state: ImportSetAdminFormState = INITIAL_STATE, action: AnyAction) => {
  const r = reducers[action.type];
  return (r && r(state, action)) || state;
};

export const reducer = withOptionsReducer(STATE.importSetAdministration, stateReducer);
